import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import TemplateImageTile from '../../components/catalog/TemplateImageTile';
import { useCatalogData } from './CatalogDataContext';
import { fallbackImage } from './catalogData';
import './catalog-detail.css';

const CollectionDetail = ({ history, location, match }) => {
  const { collectionId } = match.params;
  const { collections } = useCatalogData();

  const collection = useMemo(() => {
    if (location.state && location.state.collection) {
      return location.state.collection;
    }

    return collections.find((item) => item.id === collectionId);
  }, [collectionId, collections, location.state]);

  const artworks = useMemo(() => {
    if (!collection || !Array.isArray(collection.artworks)) {
      return [];
    }

    return collection.artworks.map((artwork, index) => {
      if (typeof artwork === 'string') {
        return {
          id: `${collection.id}-artwork-${index + 1}`,
          url: artwork,
          title: `${collection.title} artwork ${index + 1}`,
          tags: [],
        };
      }

      return {
        id: artwork.id || `${collection.id}-artwork-${index + 1}`,
        url: artwork.url || artwork.src || fallbackImage,
        title: artwork.title || `${collection.title} artwork ${index + 1}`,
        tags: Array.isArray(artwork.tags) ? artwork.tags : [],
      };
    });
  }, [collection]);

  const fallbackPreviews = useMemo(() => {
    if (!collection) {
      return [];
    }

    const basePreviews =
      (Array.isArray(collection.previews) && collection.previews.length > 0
        ? collection.previews
        : [collection.cover]) || [];

    return basePreviews
      .filter(Boolean)
      .map((preview, index) => ({
        id: `${collection.id}-preview-${index + 1}`,
        url: preview,
        title: `${collection.title} preview ${index + 1}`,
      }));
  }, [collection]);

  const artworkItems = artworks.length > 0 ? artworks : fallbackPreviews;

  const handlePromptAction = (promptText, referenceImageUrls) => {
    const trimmedPrompt = String(promptText || '').trim();
    if (!trimmedPrompt) {
      return;
    }

    history.push('/ai', {
      prompt: trimmedPrompt,
      focusTab: 'generate',
      referenceImageUrls: Array.isArray(referenceImageUrls)
        ? referenceImageUrls.filter(Boolean).slice(0, 3)
        : [],
    });
  };

  const stats = useMemo(() => {
    if (!collection) {
      return { artworks: 0, updated: 'Just now' };
    }

    const updatedLabel =
      (collection.stats && collection.stats.updated) || 'Just now';

    return {
      artworks: artworkItems.length,
      updated: updatedLabel,
    };
  }, [artworkItems.length, collection]);

  if (!collection) {
    return (
      <section className="catalog-detail catalog-detail--missing">
        <div className="catalog-detail__card">
          <h1>We couldn&apos;t find that collection</h1>
          <p>The collection may have been moved or removed. Try returning to templates and browsing again.</p>
          <div className="catalog-detail__actions">
            <button type="button" onClick={() => history.push('/templates')}>
              Back to templates
            </button>
            <Link to="/templates">Explore templates</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="catalog-detail" aria-labelledby="collection-heading">
      <div className="catalog-detail__media" aria-hidden="true">
        <img src={(artworkItems[0] && artworkItems[0].url) || collection.cover || fallbackImage} alt="" onError={(event) => {
          event.currentTarget.src = fallbackImage;
        }} />
      </div>
      <div className="catalog-detail__body">
        <p className="catalog-detail__eyebrow">{collection.curator || 'Curated collection'}</p>
        <h1 id="collection-heading">{collection.title}</h1>
        <p className="catalog-detail__description">{collection.description}</p>
        <dl className="catalog-detail__meta">
          <div>
            <dt>Artworks</dt>
            <dd>{stats.artworks}</dd>
          </div>
          <div>
            <dt>Last updated</dt>
            <dd>{stats.updated}</dd>
          </div>
        </dl>
        {collection.tags && collection.tags.length > 0 && (
          <ul className="catalog-detail__tags">
            {collection.tags.map((tag) => (
              <li key={tag}>{tag}</li>
            ))}
          </ul>
        )}
        <div className="catalog-detail__previews" aria-label="All artworks in this collection">
          {artworkItems.map((artwork) => (
            <div key={artwork.id} className="catalog-detail__preview">
              <TemplateImageTile
                imageUrl={artwork.url || fallbackImage}
                fallbackImageUrl={fallbackImage}
                title={artwork.title}
                description={artwork.prompt || artwork.title}
                shotCount={0}
                onOpen={() =>
                  handlePromptAction(
                    artwork.prompt || artwork.title,
                    artwork.url ? [artwork.url] : [],
                  )}
                onReprompt={() => handlePromptAction(artwork.prompt || artwork.title)}
                onStyleReference={() =>
                  handlePromptAction(
                    artwork.prompt || artwork.title,
                    artwork.url ? [artwork.url] : [],
                  )}
              />
              {artwork.title && <figcaption>{artwork.title}</figcaption>}
              {artwork.tags && artwork.tags.length > 0 && (
                <ul className="catalog-detail__artwork-tags">
                  {artwork.tags.map((tag) => (
                    <li key={`${artwork.id}-${tag}`}>{tag}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        <div className="catalog-detail__actions">
          <button type="button" onClick={() => history.push('/templates')}>
            Browse more templates
          </button>
          <a href="/ai" className="catalog-detail__link">
            Generate with this vibe
          </a>
        </div>
      </div>
    </section>
  );
};

export default CollectionDetail;
